import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, List, ListItem } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import useLeagueData from './useLeagueData';
import NavLink from './common/NavLink';
import useTeamCompetitionData from './useTeamCompetitionData';
import { slugify } from '../utils/slugify'; 
import { Helmet } from 'react-helmet-async'; // [cite: 394]

const mockTiles = [
  { title: 'Pozycje drużyn', description: 'Szanse na konkretne miejsca', link: (id) => `/liga/${id}/positionMatrix` },
  { title: 'Head to Head', description: 'Kto lepszy?', link: (id) => `/liga/${id}/team-comparisons` },
  { title: 'Progi punktowe', description: 'Punkty potrzebne do...', link: (id) => `/liga/${id}/points-threshold` },
  { title: 'Ranking ELO', description: '...i jego zmiany', link: (id) => `/liga/${id}/elo-ranking` },  
  { title: 'PODSUMOWANIE', description: 'Podsumowanie symulacji', link: (id) => `/liga/${id}/summary` },  
  { title: 'Ceny prądu', description: 'w tej lidze i w Twoim domu', link: '/pstryk' },
  { title: 'Własne symulacje', description: 'Stwórz własny scenariusz', link: (id) => `/liga/${id}/yourSimulation`, vip: true },
  { title: 'Zaawansowane tabele', description: 'Filtry i bilanse bezpośrednie', link: (id) => `/liga/${id}/leagueTables` },
  { title: 'Terminarz', description: 'Lista meczów', link: (id) => `/liga/${id}/fixtures` },
  { title: 'Łatwość terminarza', description: 'Kto ma najłatwiejszy terminarz?', link: (id) => `/liga/${id}/fixture_difficulty` },
  { title: 'Kalkulator tabeli', description: 'Wpisz wyniki i sprawdź tabelę', link: (id) => `/liga/${id}/standingsCalculator` },

];

const LeaguePage = () => {
  const { id } = useParams();
  const { leagueData, loading: leagueLoading, error: leagueError } = useLeagueData(id);
  const { teams, matches, leagueAchievements, relegationProbs, loading: teamsLoading, error: teamsError } = useTeamCompetitionData(id);

  const getRelegationGradientColor = (value) => {
    if (!value || value <= 0) return 'transparent';
    const powerValue = Math.pow(value, 1.0); 
    const r = 255;
    const g = 47;
    const b = 47;
    const alpha = powerValue * 0.5;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  if (!id || leagueError || teamsError) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">
          {id ? (leagueError || teamsError) : 'Błąd: Nieprawidłowy identyfikator ligi.'}
        </Typography>
      </Box>
    );
  }

  if (leagueLoading || teamsLoading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  // Przygotowanie nazwy ligi do SEO 
  const fullLeagueName = leagueData?.name 
    ? `${leagueData.name}${leagueData.group && leagueData.group.trim() ? ` (${leagueData.group.trim()})` : ''}` 
    : 'Liga';

  return (
    <>
      <Helmet>
		  <title>{`${fullLeagueName} - Symulator i tabela | SymuLiga`}</title>
		  
		  {/* W description dajemy angielską frazę "table simulator" - user widzi to tylko w Google */}
		  <meta 
			name="description" 
			content={`${fullLeagueName} table simulator. Sprawdź szanse na mistrzostwo, awans i spadek. Unikalny kalkulator wyników końcowych oparty o ranking ELO.`} 
		  />
		  <link rel="canonical" href={`https://symuliga.pl/liga/${id}`} />
		  {/* W og:title dla mediów społecznościowych również możemy dodać "simulator" */}
		  <meta property="og:title" content={`${fullLeagueName} symulator - Symulacja tabeli`} />
		  <meta property="og:description" content="Predykcje polskich lig piłkarskich. Sprawdź szanse swojej drużyny!" />
	  </Helmet>

      <Box sx={{ textAlign: 'center', padding: { xs: '5px', md: '10px' } }}>
        {/* H1 - Główny nagłówek dla SEO  */}
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          {fullLeagueName}
        </Typography>
        {leagueData?.season && (
          <Typography
            variant="caption"
            component="div"
            sx={{
              mb: 1.5,
              color: 'text.secondary',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            Sezon {leagueData.season}
          </Typography>
        )}
        
        {/* H2 - Podtytuł wspierający słowa kluczowe  */}
        <Typography variant="subtitle1" component="h2" sx={{ mb: 3, color: 'text.secondary' }}>
          Symulacja tabeli i szanse drużyn – kalkulator wyników końcowych
        </Typography>

        {/* Kafelki */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {mockTiles.map((tile, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4} // 3 kolumny na dużych ekranach (12/4 = 3)
            key={tile.title}
          >
            <Link to={typeof tile.link === 'function' ? tile.link(id) : tile.link} style={{ textDecoration: 'none' }}>
              <Card
                sx={{
                  backgroundColor: '#2c2c2c',
                  color: '#ffffff',
                  transition: 'background-color 0.3s',
                  '&:hover': { backgroundColor: '#3a3a3a' },
                  cursor: 'pointer',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    padding: '16px',
                  }}
                >
                  <Typography variant="h6">{tile.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tile.description}
                  </Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        ))}
      </Grid>

{leagueAchievements && leagueAchievements.length > 0 && (
  <Box sx={{ my: 4, px: { xs: 0, md: 0 } }}>
    <Typography
      variant="h5"
      gutterBottom
      sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}
    >
      Wybryki statystyczne ligi
    </Typography>
    <List sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, py: 1 }}>
      {leagueAchievements.map((ach) => {
        const rank = ach.rank;

        const getAchievementColor = () => {
          switch (ach.color) {
            case 'green': return '#4caf50';
            case 'red': return '#f44336';
            case 'white': return '#ffffff';
            default: return '#4caf50';
          }
        };
        const color = getAchievementColor();

        return (
          <ListItem key={ach.rankingUrl} sx={{ py: 0.8, alignItems: 'center' }}>
            {/* Ikona */}
            <Box sx={{ width: 40, display: 'flex', justifyContent: 'center' }}>
              {rank === 1 && <EmojiEventsIcon sx={{ color: '#ffd700', fontSize: 28 }} />}
              {rank === 2 && <span style={{ fontSize: '1.4rem' }}>🥈</span>}
              {rank === 3 && <span style={{ fontSize: '1.4rem' }}>🥉</span>}
            </Box>

            {/* Pozycja */}
            <Box sx={{ width: 60, textAlign: 'center' }}>
              <Typography
                component={NavLink}
                to={`/achievements/${ach.rankingUrl}`}
                sx={{
                  fontWeight: { xs: 700, md: 900 },
                  color: color,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  fontSize: { xs: '1.0rem', md: '1.2rem' },
                }}
              >
                #{ach.rank}
              </Typography>
            </Box>

            {/* Opis */}
            <Box sx={{ flex: 1 }}>
              <Typography
                component="span"
                sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}
              >
                miejsce w kategorii: {' '}
                <Typography
                  component={NavLink}
                  to={`/achievements/${ach.rankingUrl}`}
                  sx={{
                    fontWeight: { xs: 700, md: 900 },
                    color: color,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {ach.displayName}
                </Typography>
              </Typography>
            </Box>
          </ListItem>
        );
      })}
    </List>
  </Box>
)}

      {/* Tabela ligi */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Aktualna tabela - {leagueData?.name ? `${leagueData.name}${leagueData.group && leagueData.group.trim() ? ` (${leagueData.group.trim()})` : ''}` : 'Liga'}
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#2c2c2c', width: 'fit-content', margin: '0 auto' }}>
        <Table sx={{ width: 'auto' }} aria-label="league table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '8px 12px' }}>#</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '8px 12px' }}>Drużyna</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '8px 12px', textAlign: 'center' }}>Mecze</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '8px 12px', textAlign: 'center' }}>Punkty</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '8px 12px', textAlign: 'center' }}>Bramki</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team, index) => {
              const promotionSpots = leagueData?.promotion_spots || 0;
              const promotionPlayoffSpots = leagueData?.promotion_playoff_spots || 0;
			  const relegationSpots = leagueData?.relegation_spots || 0;
			  const currentPos = index + 1;
			              
			  const relegationPlayoffSpots = leagueData?.relegation_playoff_spots || 0;

              

			// 1. Logowanie surowych danych z API (tylko raz dla pierwszej drużyny)
			if (index === 0) {
			  console.log("DEBUG: Całe leagueData:", leagueData);
			  console.log("DEBUG: Obiekt relegationProbs:", leagueData?.relegationProbs);
			}

			// Pobieranie szansy
			const slotProbability = relegationProbs?.[currentPos.toString()] || 0;

			// 2. Logowanie dla konkretnych pozycji, które powinny być podświetlone
			if (slotProbability > 0 || currentPos > 14) {
			  console.log(`DEBUG: Pozycja ${currentPos}, szansa: ${slotProbability}, klucze w obiekcie:`, Object.keys(leagueData?.relegationProbs || {}));
			}

			let backgroundColor = 'transparent';

			if (index < promotionSpots) {
				backgroundColor = 'rgba(46, 125, 50, 0.3)';
			} else if (index < promotionSpots + promotionPlayoffSpots) {
				backgroundColor = 'rgba(150, 200, 80, 0.2)';
			} 
			// 3. Zastosuj dynamiczny gradient dla spadku
			else if (slotProbability > 0) {
				// Pamiętaj, aby funkcja getRelegationGradientColor była zdefiniowana w tym pliku!
				backgroundColor = getRelegationGradientColor(slotProbability / 100);
			}
			// 4. Opcjonalnie stare "sztywne" baraże (jeśli slotProbability jest 0)
			else if (index >= teams.length - (relegationSpots + relegationPlayoffSpots) && index < teams.length - relegationSpots) {
				backgroundColor = 'rgba(255, 152, 0, 0.2)';
			}

              return (
                <TableRow
                  key={team.teamId}
                  sx={{
                    backgroundColor,
                    '&:hover': { backgroundColor: backgroundColor === 'transparent' ? '#3a3a3a' : backgroundColor },
                  }}
                >
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '8px 12px' }}>{index + 1}</TableCell>
                  <TableCell component={NavLink}
					to={`/team/${team.teamId}/${slugify(team.teamName)}`} sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '8px 12px' }}>{team.teamName}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '8px 12px', textAlign: 'center' }}>{team.matchesPlayed || 0}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '8px 12px', textAlign: 'center' }}>{team.points}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '8px 12px', textAlign: 'center' }}>
                    {team.goalsScored}:{team.goalsConceded}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
	</>
  );
};

export default LeaguePage;
